import { useEffect, useMemo, useState } from "react"
import { useData } from "../data/DataContext"
import { OrgChart } from "../components/OrgChart"
import type { Objective, Person, RagScore, ReportingPeriod } from "../data/types"
import { peopleInScope } from "../data/types"

/**
 * Executive summary report.
 *
 * Two scopes:
 *  - Month: pick a single calendar month+year (default: last completed month)
 *  - Period: pick a named ReportingPeriod (default: the one marked Current)
 *
 * Print CSS hides sidebar + topbar so the print/PDF output is report-only.
 * Admin + Exec only, because it contains everyone's RAG narratives.
 */

type ReportScope = "month" | "period"

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
]

function normaliseName(s: string): string {
  return s.trim().toLowerCase().replace(/\s+/g, " ")
}

function ragLabel(v: 1 | 2 | 3 | null): "Red" | "Amber" | "Green" | "Unset" {
  if (v === 1) return "Red"
  if (v === 2) return "Amber"
  if (v === 3) return "Green"
  return "Unset"
}

/** Convert a (calendar year, calendar month 1–12) → reporting-year start year. */
function calendarToFiscalYear(y: number, m: number): number {
  return m >= 4 ? y : y - 1
}

/**
 * Current calendar month, represented as (year, month 1–12). Used as the
 * default for the month picker so the user lands on the period they're
 * actively working in — prior Claude defaulted to last-completed-month
 * on the "partial data" theory, but Chris prefers to land on the current
 * month and let users pick backwards if they need the settled view.
 */
function currentCalendarMonth(now: Date): { year: number; month: number } {
  return { year: now.getFullYear(), month: now.getMonth() + 1 }
}

function formatLongDate(s: string | null): string {
  if (!s) return "—"
  const d = new Date(s + "T00:00:00Z")
  if (isNaN(d.getTime())) return s
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  })
}

/**
 * Calendar months covered by a reporting period, inclusive at both ends.
 * Returns [{calendarYear, month 1-12}, ...] from startDate's month to
 * endDate's month. Empty if either date is null/invalid.
 */
function monthsInPeriod(
  period: ReportingPeriod | null,
): Array<{ calendarYear: number; month: number }> {
  if (!period || !period.startDate || !period.endDate) return []
  const start = new Date(period.startDate + "T00:00:00Z")
  const end = new Date(period.endDate + "T00:00:00Z")
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return []
  const out: Array<{ calendarYear: number; month: number }> = []
  const cur = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 1))
  const endMarker = new Date(
    Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), 1),
  )
  // Hard cap to prevent accidental runaway loops on bad data
  for (let i = 0; i < 120 && cur <= endMarker; i++) {
    out.push({ calendarYear: cur.getUTCFullYear(), month: cur.getUTCMonth() + 1 })
    cur.setUTCMonth(cur.getUTCMonth() + 1)
  }
  return out
}

export function ReportsPage() {
  const { people, objectives, ragScores, reportingPeriods, me } = useData()

  // Role gate — the report contains narrative comments from multiple
  // objective holders. Admin / Exec / CLT all welcome (CLT defaults to My Team).
  if (me.role !== "Admin" && me.role !== "Exec" && me.role !== "CLT") {
    return (
      <div className="rounded-lg border border-rag-amber bg-rag-amber-bg p-8 shadow-card">
        <div className="mb-2 font-mono text-[10px] uppercase tracking-[0.14em] text-rag-amber-ink">
          Not authorised
        </div>
        <h2 className="mb-3 font-display text-[22px] font-medium tracking-[-0.01em] text-ink">
          Reports restricted
        </h2>
        <p className="max-w-xl text-[14px] leading-[1.6] text-ink-2">
          The report pages contain narrative comments for objective holders.
          They're available to Admin, Exec and CLT roles.
        </p>
      </div>
    )
  }

  const initialMonth = useMemo(() => currentCalendarMonth(new Date()), [])
  // CLT defaults to My Team; Admin/Exec default to the full board Overview.
  // "Personal" is a fifth option that shows only the signed-in user's
  // objectives — useful for personal supervision/coaching docs and 1:1s.
  // "People I Cover" shows self + everyone in me.covers — any team,
  // including Users. Empty report if you cover nobody. Scope semantics for
  // all four reportable modes come from the shared peopleInScope() helper.
  const [viewMode, setViewMode] = useState<
    "personal" | "myTeam" | "peopleICover" | "execOverview" | "orgChart"
  >(me.role === "CLT" ? "myTeam" : "execOverview")
  // Only affects Exec Overview mode. When true (default), Team="User" self-
  // service people are excluded from the board-facing report.
  const [excludeUsers, setExcludeUsers] = useState(true)
  const [scope, setScope] = useState<ReportScope>("month")
  const [year, setYear] = useState(initialMonth.year)
  const [month, setMonth] = useState(initialMonth.month)
  const [selectedPeriodId, setSelectedPeriodId] = useState<number | null>(null)

  // Default the period picker to the Current period (fallback: most recent by start date)
  useEffect(() => {
    if (scope !== "period" || selectedPeriodId != null) return
    if (reportingPeriods.length === 0) return
    const current = reportingPeriods.find((p) => p.isCurrent)
    const fallback = [...reportingPeriods].sort(
      (a, b) => (b.startDate ?? "").localeCompare(a.startDate ?? ""),
    )[0]
    const pick = current ?? fallback
    if (pick) setSelectedPeriodId(pick.id)
  }, [scope, selectedPeriodId, reportingPeriods])

  const selectedPeriod = useMemo(
    () =>
      scope === "period" && selectedPeriodId != null
        ? reportingPeriods.find((p) => p.id === selectedPeriodId) ?? null
        : null,
    [scope, selectedPeriodId, reportingPeriods],
  )

  // Month-mode derived bits
  const fiscalYear = calendarToFiscalYear(year, month)
  const monthModeLabel = `${MONTH_NAMES[month - 1]} ${year}`
  const fiscalLabel = `${fiscalYear}/${String((fiscalYear + 1) % 100).padStart(2, "0")}`

  // Scope-aware report header + filter
  const scopeLabel =
    scope === "period"
      ? selectedPeriod?.title ?? "(no period selected)"
      : monthModeLabel

  // Year picker values
  const availableYears = useMemo(() => {
    const now = new Date()
    const s = new Set<number>([now.getFullYear(), now.getFullYear() - 1])
    for (const r of ragScores) {
      s.add(r.year)
      s.add(r.year + 1)
    }
    return [...s].sort((a, b) => b - a)
  }, [ragScores])

  // Period picker options — sort current first, then by start date desc
  const periodOptions = useMemo(() => {
    return [...reportingPeriods].sort((a, b) => {
      if (a.isCurrent !== b.isCurrent) return a.isCurrent ? -1 : 1
      return (b.startDate ?? "").localeCompare(a.startDate ?? "")
    })
  }, [reportingPeriods])

  /* ----- Derived data for the selected scope ----- */

  const personByName = useMemo(() => {
    const m = new Map<string, Person>()
    for (const p of people) m.set(normaliseName(p.fullName), p)
    return m
  }, [people])

  // Normalised names of people in scope for the selected view. Delegated to
  // the shared `peopleInScope` helper in data/types so /team and /reports
  // stay in lock-step on scope semantics.
  //   - personal:     just me
  //   - myTeam:       self + same-department active + people I cover
  //   - peopleICover: self + everyone in me.covers (any team); empty if none
  //   - execOverview: active Exec/CLT; plus Team="User" when !excludeUsers
  //   - orgChart:     unused (OrgChart consumes the full people list directly)
  const reportableNames = useMemo(() => {
    if (viewMode === "orgChart") return new Set<string>()
    return peopleInScope(
      people,
      me.matchedPerson ?? null,
      me.covers,
      viewMode,
      !excludeUsers,
    )
  }, [people, viewMode, me.matchedPerson, me.covers, excludeUsers])

  const objectiveByKey = useMemo(() => {
    const m = new Map<string, Objective>()
    for (const o of objectives) {
      if (o.status === "Deleted" || !o.category) continue
      m.set(`${normaliseName(o.personName)}|${o.category}`, o)
    }
    return m
  }, [objectives])

  // Scores in scope — the core filter. Team="User" scores are stripped out
  // so they never enter the board report even if someone’s scored them.
  const reportScores = useMemo(() => {
    const base = ragScores.filter((r) =>
      reportableNames.has(normaliseName(r.personName)),
    )
    if (scope === "month") {
      return base.filter(
        (r) => r.year === fiscalYear && r.month === month,
      )
    }
    // period mode
    if (!selectedPeriod || !selectedPeriod.startDate || !selectedPeriod.endDate) {
      return []
    }
    const start = selectedPeriod.startDate
    const end = selectedPeriod.endDate
    return base.filter((r) => {
      if (r.month < 1 || r.month > 12) return false
      const calendarYear = r.month >= 4 ? r.year : r.year + 1
      const scoreDate = `${calendarYear}-${String(r.month).padStart(2, "0")}-15`
      return start <= scoreDate && scoreDate <= end
    })
  }, [scope, ragScores, fiscalYear, month, selectedPeriod, reportableNames])

  // Stable sort for section rendering
  const sortedScores = useMemo(
    () =>
      [...reportScores].sort(
        (a, b) =>
          a.personName.localeCompare(b.personName) ||
          a.year - b.year ||
          a.month - b.month ||
          a.category.localeCompare(b.category),
      ),
    [reportScores],
  )

  const redScores = sortedScores.filter((s) => s.ragValue === 1)
  const amberScores = sortedScores.filter((s) => s.ragValue === 2)
  const greenScores = sortedScores.filter((s) => s.ragValue === 3)
  const scoredCount = reportScores.filter((s) => s.ragValue != null).length

  // Months covered by the current scope (1 for month, N for period)
  const scopedMonths = useMemo(() => {
    if (scope === "month") return [{ calendarYear: year, month }]
    return monthsInPeriod(selectedPeriod)
  }, [scope, year, month, selectedPeriod])

  // Expected = for each month-in-scope, the count of active in-scope objectives
  // for that month's fiscal year. Sum across months. Only Exec/CLT objectives
  // count — Team="User" self-service people are out of scope for the board.
  const { totalInScope, gapKeys } = useMemo(() => {
    let total = 0
    // Track (person, category, calYear, month) pairs that *should* have a score
    const expected = new Set<string>()
    for (const { calendarYear, month: m } of scopedMonths) {
      const fy = calendarToFiscalYear(calendarYear, m)
      for (const o of objectives) {
        if (o.status === "Deleted" || !o.category) continue
        if (o.year !== fy) continue
        if (!reportableNames.has(normaliseName(o.personName))) continue
        total++
        expected.add(
          `${normaliseName(o.personName)}|${o.category}|${calendarYear}|${m}`,
        )
      }
    }
    // Compute actually-scored keys in the same shape for set-diff
    const scoredSlotKeys = new Set<string>()
    for (const s of reportScores) {
      const calendarYear = s.month >= 4 ? s.year : s.year + 1
      scoredSlotKeys.add(
        `${normaliseName(s.personName)}|${s.category}|${calendarYear}|${s.month}`,
      )
    }
    const gaps = new Set<string>()
    for (const k of expected) if (!scoredSlotKeys.has(k)) gaps.add(k)
    return { totalInScope: total, gapKeys: gaps }
  }, [objectives, scopedMonths, reportScores, reportableNames])

  const coveragePct =
    totalInScope > 0 ? Math.round((scoredCount / totalInScope) * 100) : 0
  const unsetCount = Math.max(0, totalInScope - scoredCount)

  // Build gap list from gapKeys — flatten back to tuples for display
  const missingScores = useMemo(() => {
    const rows: Array<{
      personName: string
      category: string
      calendarYear: number
      month: number
    }> = []
    for (const k of gapKeys) {
      const [personNorm, category, yearStr, monthStr] = k.split("|")
      rows.push({
        personName: personNorm, // normalised; for display we could resolve to real case, but this is fine
        category,
        calendarYear: Number(yearStr),
        month: Number(monthStr),
      })
    }
    // Resolve display names from the People list
    const realName = new Map<string, string>()
    for (const p of people) realName.set(normaliseName(p.fullName), p.fullName)
    return rows
      .map((r) => ({
        ...r,
        personName: realName.get(r.personName) ?? r.personName,
      }))
      .sort(
        (a, b) =>
          a.personName.localeCompare(b.personName) ||
          a.category.localeCompare(b.category) ||
          a.calendarYear - b.calendarYear ||
          a.month - b.month,
      )
  }, [gapKeys, people])

  // By-department grouping of everyone who has at least one score in scope
  const byDepartment = useMemo(() => {
    const groups = new Map<
      string,
      Map<number, { person: Person; scores: RagScore[] }>
    >()
    for (const s of sortedScores) {
      const person = personByName.get(normaliseName(s.personName))
      if (!person || !person.active) continue
      const dept = person.department ?? "Unassigned"
      let deptMap = groups.get(dept)
      if (!deptMap) {
        deptMap = new Map()
        groups.set(dept, deptMap)
      }
      let entry = deptMap.get(person.id)
      if (!entry) {
        entry = { person, scores: [] }
        deptMap.set(person.id, entry)
      }
      entry.scores.push(s)
    }
    return [...groups.entries()]
      .map(
        ([dept, m]) =>
          [
            dept,
            [...m.values()].sort((a, b) =>
              a.person.fullName.localeCompare(b.person.fullName),
            ),
          ] as const,
      )
      .sort((a, b) => a[0].localeCompare(b[0]))
  }, [sortedScores, personByName])

  const handlePrint = () => window.print()

  const generatedAt = new Date().toLocaleString("en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
  })

  const noPeriodsYet = reportingPeriods.length === 0

  return (
    <div>
      {/* Controls — hidden in print */}
      <div className="mb-5 flex flex-wrap items-end justify-between gap-4 print:hidden">
        <div className="flex flex-wrap items-end gap-3">
          {/* View toggle */}
          <div className="flex flex-col gap-[6px]">
            <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-ink-3">
              View
            </span>
            <div className="inline-flex rounded-sm border border-rule-strong bg-card p-[2px]">
              <button
                type="button"
                onClick={() => setViewMode("personal")}
                className={[
                  "rounded-sm px-[12px] py-[5px] font-mono text-[10.5px] uppercase tracking-[0.12em] transition",
                  viewMode === "personal"
                    ? "bg-nymr-navy text-white"
                    : "text-ink-2 hover:text-ink",
                ].join(" ")}
              >
                Personal
              </button>
              <button
                type="button"
                onClick={() => setViewMode("myTeam")}
                className={[
                  "rounded-sm px-[12px] py-[5px] font-mono text-[10.5px] uppercase tracking-[0.12em] transition",
                  viewMode === "myTeam"
                    ? "bg-nymr-navy text-white"
                    : "text-ink-2 hover:text-ink",
                ].join(" ")}
              >
                My Team
              </button>
              <button
                type="button"
                onClick={() => setViewMode("peopleICover")}
                className={[
                  "rounded-sm px-[12px] py-[5px] font-mono text-[10.5px] uppercase tracking-[0.12em] transition",
                  viewMode === "peopleICover"
                    ? "bg-nymr-navy text-white"
                    : "text-ink-2 hover:text-ink",
                ].join(" ")}
              >
                People I Cover
              </button>
              <button
                type="button"
                onClick={() => setViewMode("execOverview")}
                className={[
                  "rounded-sm px-[12px] py-[5px] font-mono text-[10.5px] uppercase tracking-[0.12em] transition",
                  viewMode === "execOverview"
                    ? "bg-nymr-navy text-white"
                    : "text-ink-2 hover:text-ink",
                ].join(" ")}
              >
                Exec Overview
              </button>
              <button
                type="button"
                onClick={() => setViewMode("orgChart")}
                className={[
                  "rounded-sm px-[12px] py-[5px] font-mono text-[10.5px] uppercase tracking-[0.12em] transition",
                  viewMode === "orgChart"
                    ? "bg-nymr-navy text-white"
                    : "text-ink-2 hover:text-ink",
                ].join(" ")}
              >
                Org Chart
              </button>
            </div>
          </div>

          {viewMode !== "orgChart" ? (
            <>
              {/* Scope toggle */}
          <div className="flex flex-col gap-[6px]">
            <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-ink-3">
              Scope
            </span>
            <div className="inline-flex rounded-sm border border-rule-strong bg-card p-[2px]">
              <button
                type="button"
                onClick={() => setScope("month")}
                className={[
                  "rounded-sm px-[12px] py-[5px] font-mono text-[10.5px] uppercase tracking-[0.12em] transition",
                  scope === "month"
                    ? "bg-nymr-navy text-white"
                    : "text-ink-2 hover:text-ink",
                ].join(" ")}
              >
                Month
              </button>
              <button
                type="button"
                onClick={() => setScope("period")}
                disabled={noPeriodsYet}
                title={noPeriodsYet ? "No reporting periods defined — add some under Admin → Reporting periods" : undefined}
                className={[
                  "rounded-sm px-[12px] py-[5px] font-mono text-[10.5px] uppercase tracking-[0.12em] transition disabled:opacity-40",
                  scope === "period"
                    ? "bg-nymr-navy text-white"
                    : "text-ink-2 hover:text-ink",
                ].join(" ")}
              >
                Period
              </button>
            </div>
          </div>

          {scope === "month" ? (
            <>
              <label className="flex flex-col gap-[6px]">
                <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-ink-3">
                  Month
                </span>
                <select
                  value={month}
                  onChange={(e) => setMonth(Number(e.target.value))}
                  className="rounded-sm border border-rule-strong bg-card px-[10px] py-2 text-[13px] text-ink focus:border-nymr-navy focus:outline-none"
                >
                  {MONTH_NAMES.map((n, i) => (
                    <option key={n} value={i + 1}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-[6px]">
                <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-ink-3">
                  Year
                </span>
                <select
                  value={year}
                  onChange={(e) => setYear(Number(e.target.value))}
                  className="rounded-sm border border-rule-strong bg-card px-[10px] py-2 text-[13px] text-ink focus:border-nymr-navy focus:outline-none"
                >
                  {availableYears.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </label>
              <div className="ml-2 text-[12px] text-ink-3">
                Reporting year {fiscalLabel}
              </div>
            </>
          ) : (
            <label className="flex flex-col gap-[6px]">
              <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-ink-3">
                Reporting period
              </span>
              <select
                value={selectedPeriodId ?? ""}
                onChange={(e) =>
                  setSelectedPeriodId(
                    e.target.value ? Number(e.target.value) : null,
                  )
                }
                className="rounded-sm border border-rule-strong bg-card px-[10px] py-2 text-[13px] text-ink focus:border-nymr-navy focus:outline-none"
              >
                {periodOptions.length === 0 ? (
                  <option value="">(none defined)</option>
                ) : null}
                {periodOptions.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.title}
                    {p.isCurrent ? "  (current)" : ""}
                  </option>
                ))}
              </select>
            </label>
          )}

          {viewMode === "execOverview" ? (
            <label className="ml-1 inline-flex cursor-pointer items-center gap-[8px] rounded-sm border border-rule-strong bg-card px-[12px] py-[8px] font-mono text-[10.5px] uppercase tracking-[0.12em] text-ink-2 transition hover:text-ink">
              <input
                type="checkbox"
                checked={excludeUsers}
                onChange={(e) => setExcludeUsers(e.target.checked)}
                className="h-[14px] w-[14px] accent-nymr-navy"
              />
              Exclude Users
            </label>
          ) : null}
            </>
          ) : null}
        </div>

        <button
          type="button"
          onClick={handlePrint}
          className="inline-flex items-center gap-2 rounded-sm bg-nymr-navy px-[14px] py-2 font-mono text-[11px] uppercase tracking-[0.14em] text-white transition hover:bg-nymr-navy-dark"
        >
          <svg viewBox="0 0 20 20" fill="none" className="h-3.5 w-3.5">
            <path
              d="M6 3h8v5H6Zm-1 6h10v6H5Zm1 6v3h8v-3"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinejoin="round"
            />
          </svg>
          Print / Save as PDF
        </button>
      </div>

      {/* Report — this is the print body */}
      {viewMode === "orgChart" ? (
        <article className="rounded-lg border border-rule bg-card p-[40px] shadow-card print:rounded-none print:border-0 print:bg-white print:p-0 print:shadow-none">
          <header className="mb-8 border-b border-rule pb-6">
            <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-brass">
              NYMR · Org Chart
            </div>
            <h1 className="mt-[6px] font-display text-[32px] font-medium tracking-[-0.01em] text-ink">
              Reporting lines
            </h1>
            <p className="mt-[6px] text-[13px] text-ink-2">
              Active people, grouped by level. Wires follow the CoveredBy field
              — Exec at the top, then their CLT leaders, then any Users covered
              by each CLT.
            </p>
          </header>

          <OrgChart people={people} />

          <footer className="mt-10 border-t border-rule pt-4 font-mono text-[10px] uppercase tracking-[0.12em] text-ink-3">
            Generated {generatedAt} · NYMR Objectives · objectives.nymr.co.uk
          </footer>
        </article>
      ) : (
      <article className="rounded-lg border border-rule bg-card p-[40px] shadow-card print:rounded-none print:border-0 print:bg-white print:p-0 print:shadow-none">
        {/* Header */}
        <header className="mb-8 border-b border-rule pb-6">
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-brass">
            NYMR ·{" "}
            {viewMode === "personal"
              ? "Personal Report"
              : viewMode === "myTeam"
                ? "My Team Report"
                : viewMode === "peopleICover"
                  ? "People I Cover Report"
                  : "Executive Summary"}
          </div>
          <h1 className="mt-[6px] font-display text-[32px] font-medium tracking-[-0.01em] text-ink">
            {scopeLabel}
          </h1>
          <p className="mt-[6px] text-[13px] text-ink-2">
            {viewMode === "personal"
              ? "My objectives"
              : viewMode === "myTeam"
                ? "My objectives, my department and people I cover"
                : viewMode === "peopleICover"
                  ? "My objectives and people I cover"
                  : "Prepared for the CEO"}{" "}
            · Objectives & RAG performance ·{" "}
            {scope === "period" ? (
              <>
                Reporting period{" "}
                {selectedPeriod?.year != null ? (
                  <span className="font-medium text-ink">
                    {selectedPeriod.year}
                  </span>
                ) : null}
              </>
            ) : (
              <>
                Reporting year{" "}
                <span className="font-medium text-ink">{fiscalLabel}</span>
              </>
            )}
          </p>
          {scope === "period" && selectedPeriod?.boardMeetingDate ? (
            <p className="mt-2 text-[12.5px] text-ink-2">
              Board meeting{" "}
              <span className="font-medium text-ink">
                {formatLongDate(selectedPeriod.boardMeetingDate)}
              </span>
              {selectedPeriod.boardDeadlineDate ? (
                <>
                  {" "}· submissions due{" "}
                  <span className="font-medium text-ink">
                    {formatLongDate(selectedPeriod.boardDeadlineDate)}
                  </span>
                </>
              ) : null}
            </p>
          ) : null}
        </header>

        {/* At a glance */}
        <section className="mb-8 break-inside-avoid">
          <h2 className="mb-3 font-mono text-[11px] uppercase tracking-[0.14em] text-ink-3">
            At a glance
            {scope === "period" && scopedMonths.length > 0 ? (
              <span className="ml-2 normal-case tracking-normal text-ink-2">
                ({scopedMonths.length} month
                {scopedMonths.length === 1 ? "" : "s"})
              </span>
            ) : null}
          </h2>
          <div className="grid grid-cols-4 gap-3">
            <StatBox
              label="Scored"
              value={`${scoredCount}`}
              sub={totalInScope > 0 ? `of ${totalInScope}` : undefined}
            />
            <StatBox
              label="Coverage"
              value={totalInScope > 0 ? `${coveragePct}%` : "—"}
            />
            <StatBox
              label="Red"
              value={String(redScores.length)}
              tone={redScores.length > 0 ? "red" : "neutral"}
            />
            <StatBox
              label="Amber"
              value={String(amberScores.length)}
              tone={amberScores.length > 0 ? "amber" : "neutral"}
            />
          </div>
          <RagBar
            red={redScores.length}
            amber={amberScores.length}
            green={greenScores.length}
            unset={unsetCount}
          />
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 font-mono text-[10.5px] uppercase tracking-[0.1em] text-ink-3">
            <LegendDot color="bg-rag-green" label={`${greenScores.length} green`} />
            <LegendDot color="bg-rag-amber" label={`${amberScores.length} amber`} />
            <LegendDot color="bg-rag-red" label={`${redScores.length} red`} />
            <LegendDot color="bg-rag-unset" label={`${unsetCount} not scored`} />
          </div>
        </section>

        {/* Attention (Red) */}
        <Section
          title="Attention required"
          count={redScores.length}
          badge={`${redScores.length} red`}
          titleTone="red"
          empty="No red scores in this period."
        >
          {redScores.map((s) => (
            <NarrativeCard
              key={s.id}
              score={s}
              person={personByName.get(normaliseName(s.personName)) ?? null}
              objective={
                objectiveByKey.get(
                  `${normaliseName(s.personName)}|${s.category}`,
                ) ?? null
              }
              accent="red"
              showDate={scope === "period"}
            />
          ))}
        </Section>

        {/* Watch list (Amber) */}
        <Section
          title="Watch list"
          count={amberScores.length}
          badge={`${amberScores.length} amber`}
          titleTone="amber"
          empty="No amber scores in this period."
        >
          {amberScores.map((s) => (
            <NarrativeCard
              key={s.id}
              score={s}
              person={personByName.get(normaliseName(s.personName)) ?? null}
              objective={
                objectiveByKey.get(
                  `${normaliseName(s.personName)}|${s.category}`,
                ) ?? null
              }
              accent="amber"
              showDate={scope === "period"}
            />
          ))}
        </Section>

        {/* On track (Green) narratives — only those with comments */}
        {greenScores.filter((s) => s.narrative && s.narrative.trim() !== "")
          .length > 0 ? (
          <Section
            title="On track — notable progress"
            badge={`${greenScores.filter((s) => s.narrative && s.narrative.trim() !== "").length} green with notes`}
            titleTone="green"
          >
            {greenScores
              .filter((s) => s.narrative && s.narrative.trim() !== "")
              .map((s) => (
                <NarrativeCard
                  key={s.id}
                  score={s}
                  person={personByName.get(normaliseName(s.personName)) ?? null}
                  objective={
                    objectiveByKey.get(
                      `${normaliseName(s.personName)}|${s.category}`,
                    ) ?? null
                  }
                  accent="green"
                  showDate={scope === "period"}
                />
              ))}
          </Section>
        ) : null}

        {/* By department */}
        <section className="mb-8">
          <h2 className="mb-4 font-display text-[20px] font-medium tracking-[-0.005em] text-ink">
            By department
          </h2>
          {byDepartment.length > 0 ? (
            <div className="flex flex-col gap-6">
              {byDepartment.map(([dept, entries]) => (
                <div key={dept} className="break-inside-avoid">
                  <h3 className="mb-2 border-b border-rule pb-1 font-display text-[15px] font-medium tracking-[-0.005em] text-ink">
                    {dept}
                  </h3>
                  <div className="mt-2 flex flex-col gap-2">
                    {entries.map(({ person, scores }) => (
                      <div
                        key={person.id}
                        className="rounded-md border border-rule bg-paper-sunk/40 p-3"
                      >
                        <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
                          <div className="font-medium text-ink">
                            {person.fullName}
                          </div>
                          {person.jobTitle ? (
                            <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-ink-3">
                              {person.jobTitle}
                            </div>
                          ) : null}
                        </div>
                        <ul className="flex flex-col gap-[6px]">
                          {scores.map((s) => (
                            <li key={s.id} className="text-[12.5px] leading-[1.5]">
                              <div className="flex items-baseline gap-2">
                                <RagPill value={s.ragValue} />
                                <span className="font-medium text-ink">
                                  {s.category}
                                </span>
                                {scope === "period" ? (
                                  <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-ink-3">
                                    {MONTH_NAMES[s.month - 1]?.slice(0, 3) ?? s.month}
                                  </span>
                                ) : null}
                              </div>
                              <div className="ml-[1px] mt-[2px] pl-1 text-ink-2">
                                {s.narrative && s.narrative.trim() !== "" ? (
                                  s.narrative
                                ) : (
                                  <em className="text-ink-3">
                                    No comment recorded.
                                  </em>
                                )}
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="italic text-ink-3">
              No scores recorded by any department in this{" "}
              {scope === "period" ? "period" : "month"}.
            </p>
          )}
        </section>

        {/* Gaps */}
        {missingScores.length > 0 ? (
          <section className="mb-8 break-inside-avoid">
            <div className="mb-3 flex items-baseline justify-between">
              <h2 className="font-display text-[20px] font-medium tracking-[-0.005em] text-ink">
                Gaps
              </h2>
              <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-ink-3">
                {missingScores.length} not scored
              </span>
            </div>
            <p className="mb-3 text-[12.5px] text-ink-2">
              Active objectives without a RAG score in{" "}
              <span className="font-medium text-ink">{scopeLabel}</span>
              {scope === "period" && scopedMonths.length > 1 ? (
                <> (per month)</>
              ) : null}
              .
            </p>
            <ul className="grid grid-cols-1 gap-x-6 text-[12.5px] text-ink-2 sm:grid-cols-2">
              {missingScores.map((r, i) => (
                <li key={i} className="mb-1 break-inside-avoid">
                  <span className="font-medium text-ink">{r.personName}</span>{" "}
                  <span className="text-ink-3">·</span> {r.category}
                  {scope === "period" && scopedMonths.length > 1 ? (
                    <span className="ml-1 text-ink-3">
                      ({MONTH_NAMES[r.month - 1]?.slice(0, 3) ?? r.month}{" "}
                      {r.calendarYear})
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {/* Footer */}
        <footer className="mt-10 border-t border-rule pt-4 font-mono text-[10px] uppercase tracking-[0.12em] text-ink-3">
          Generated {generatedAt} · NYMR Objectives · objectives.nymr.co.uk
        </footer>
      </article>
      )}
    </div>
  )
}

/* ---------- Small components ---------- */

function StatBox({
  label,
  value,
  sub,
  tone,
}: {
  label: string
  value: string
  sub?: string
  tone?: "red" | "amber" | "neutral"
}) {
  const valueCls =
    tone === "red"
      ? "text-rag-red"
      : tone === "amber"
        ? "text-rag-amber-ink"
        : "text-ink"
  return (
    <div className="rounded-md border border-rule bg-paper-sunk p-3">
      <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-ink-3">
        {label}
      </div>
      <div
        className={[
          "mt-1 font-display text-[26px] font-medium tracking-[-0.01em]",
          valueCls,
        ].join(" ")}
      >
        {value}
        {sub ? (
          <span className="ml-1 font-sans text-[12px] font-normal text-ink-3">
            {sub}
          </span>
        ) : null}
      </div>
    </div>
  )
}

function RagBar({
  red,
  amber,
  green,
  unset,
}: {
  red: number
  amber: number
  green: number
  unset: number
}) {
  const total = red + amber + green + unset
  if (total === 0) {
    return (
      <div className="mt-3 h-3 rounded-full border border-dashed border-rule-strong bg-paper-sunk" />
    )
  }
  const pct = (n: number) => (n / total) * 100
  return (
    <div className="mt-3 flex h-3 overflow-hidden rounded-full border border-rule bg-paper-sunk print:border-[#d3cbb2]">
      {green > 0 ? (
        <div className="h-full bg-rag-green" style={{ width: `${pct(green)}%` }} />
      ) : null}
      {amber > 0 ? (
        <div className="h-full bg-rag-amber" style={{ width: `${pct(amber)}%` }} />
      ) : null}
      {red > 0 ? (
        <div className="h-full bg-rag-red" style={{ width: `${pct(red)}%` }} />
      ) : null}
      {unset > 0 ? (
        <div className="h-full bg-rag-unset" style={{ width: `${pct(unset)}%` }} />
      ) : null}
    </div>
  )
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-[6px]">
      <span className={["inline-block h-[8px] w-[8px] rounded-full", color].join(" ")} />
      {label}
    </span>
  )
}

function RagPill({ value }: { value: 1 | 2 | 3 | null }) {
  const label = ragLabel(value)
  const cls =
    value === 1
      ? "bg-rag-red text-white"
      : value === 2
        ? "bg-rag-amber text-ink"
        : value === 3
          ? "bg-rag-green text-white"
          : "bg-rag-unset text-ink-3"
  return (
    <span
      className={[
        "inline-flex items-center rounded-full px-[8px] py-[1px] font-mono text-[9px] uppercase tracking-[0.12em]",
        cls,
      ].join(" ")}
    >
      {label}
    </span>
  )
}

function Section({
  title,
  count,
  badge,
  titleTone,
  empty,
  children,
}: {
  title: string
  count?: number
  badge?: string
  titleTone?: "red" | "amber" | "green"
  empty?: string
  children?: React.ReactNode
}) {
  const hasContent = Array.isArray(children) ? children.length > 0 : !!children
  if (!hasContent && !empty) return null

  const titleCls =
    titleTone === "red"
      ? "text-rag-red"
      : titleTone === "amber"
        ? "text-rag-amber-ink"
        : titleTone === "green"
          ? "text-rag-green"
          : "text-ink"

  return (
    <section className="mb-8 break-inside-avoid">
      <div className="mb-3 flex items-baseline justify-between gap-2">
        <h2
          className={[
            "font-display text-[20px] font-medium tracking-[-0.005em]",
            titleCls,
          ].join(" ")}
        >
          {title}
          {count != null ? (
            <span className="ml-2 font-mono text-[11px] uppercase tracking-[0.14em] text-ink-3">
              {count}
            </span>
          ) : null}
        </h2>
        {badge ? (
          <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-ink-3">
            {badge}
          </span>
        ) : null}
      </div>
      {hasContent ? (
        <div className="flex flex-col gap-3">{children}</div>
      ) : (
        <p className="italic text-ink-3">{empty}</p>
      )}
    </section>
  )
}

function NarrativeCard({
  score,
  person,
  objective,
  accent,
  showDate,
}: {
  score: RagScore
  person: Person | null
  objective: Objective | null
  accent: "red" | "amber" | "green"
  showDate?: boolean
}) {
  const borderCls =
    accent === "red"
      ? "border-rag-red"
      : accent === "amber"
        ? "border-rag-amber"
        : "border-rag-green"
  const bgCls =
    accent === "red"
      ? "bg-rag-red-bg"
      : accent === "amber"
        ? "bg-rag-amber-bg"
        : "bg-rag-green-bg"
  const pillCls =
    accent === "red"
      ? "bg-rag-red text-white"
      : accent === "amber"
        ? "bg-rag-amber text-ink"
        : "bg-rag-green text-white"

  const calendarYear = score.month >= 4 ? score.year : score.year + 1
  const whenLabel =
    showDate && score.month >= 1 && score.month <= 12
      ? `${MONTH_NAMES[score.month - 1]} ${calendarYear}`
      : null

  return (
    <div
      className={[
        "break-inside-avoid rounded-md border p-4",
        borderCls,
        bgCls,
      ].join(" ")}
    >
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <div className="flex flex-col">
          <div className="font-medium text-ink">{score.personName}</div>
          {person ? (
            <div className="text-[12px] text-ink-2">
              {person.department ?? "—"}
              {person.jobTitle ? ` · ${person.jobTitle}` : ""}
            </div>
          ) : (
            <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-rag-amber-ink">
              Unmatched person
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span
            className={[
              "inline-flex items-center rounded-full px-[8px] py-[1px] font-mono text-[9px] uppercase tracking-[0.12em]",
              pillCls,
            ].join(" ")}
          >
            {ragLabel(score.ragValue)}
          </span>
          <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-ink-2">
            {score.category}
          </div>
          {whenLabel ? (
            <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-ink-3">
              · {whenLabel}
            </div>
          ) : null}
        </div>
      </div>
      {objective?.objectiveText ? (
        <div className="mb-2 text-[12px] italic leading-[1.5] text-ink-2">
          {objective.objectiveText}
        </div>
      ) : null}
      <div className="rounded-sm border border-rule bg-card p-[10px] text-[13px] leading-[1.55] text-ink-2">
        {score.narrative && score.narrative.trim() !== "" ? (
          score.narrative
        ) : (
          <em className="text-ink-3">No comment recorded.</em>
        )}
      </div>
    </div>
  )
}
